import { yupResolver } from "@hookform/resolvers/yup";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { LoadingButton } from "@mui/lab";
import { Autocomplete, Box, Checkbox, Dialog, DialogActions, DialogContent, FormControl, FormControlLabel, FormHelperText, Grid, Radio, RadioGroup, TextField } from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useAsyncDebounce } from "react-table";
import { toast } from "react-toastify";
import { CommitteeService } from "../../../../Component/Api/EmploymentEquity/Committee/CommitteeService";
import { PeopleService } from "../../../../Component/Api/People/peopleService";
import { MDBox } from "../../../../Component/Common/Components/MDBox";
import { MDButton } from "../../../../Component/Common/Components/MDButton";
import { MDTypography } from "../../../../Component/Common/Components/MDTypography";
import { CommitteeMember } from "../../../../Component/Model/Dto/EmploymentEquity/Committee";
import { PeopleDtos } from "../../../../Component/Model/Dto/People";
import { CommitteeType } from "../../../../Component/Model/Enum/EmploymentEquity";
import { occupationLevelFormatter, offsetTimezone } from "../../../../Component/Model/helpers";
import { emptyGuid } from "../../../../Helpers/typescriptHelper";
import { CustomDialogTitle } from "../../../Common/Custom/CustomDialogTitle";
import { SupportingDocuments } from "../../../Common/SupportingDocuments/SupportingDocuments";
import { committeeMemberPersonSchema } from "./CommitteeMemberPersonValidation";
import { getDateWithoutTime } from "../../../../Helpers/DateFormatter";
import { ValidatedMobileDatePicker } from "../../../../Component/Common/Components/ValidatedMobileDatePicker/ValidatedMobileDatePicker";

interface IProps {
    id?: string;
    scorecardId: string;
    link: string;
    previewPopup: boolean;
    committeeType: CommitteeType;
    handleCancel: () => void;
    refreshTable: () => void;
    setMemberId: (id: string) => void;

}
interface IDropDownOptions {
    id: string,
    value: boolean
}

export const CommitteeMemberPersonModal = (props: IProps) => {

    const [documentsPopUp, setDocumentsPopUp] = useState<boolean>(false);
    const [memberId, setMemberId] = useState<string | undefined>(props.id);
    const [occupationalLevel, setOccupationalLevel] = useState<string | undefined>();
    const [jobTitle, setJobTitle] = useState<string | undefined>();
    const [showResignedDate, setShowResignedDate] = useState<boolean>(false);
    const [peopleSearchValue, setPeopleSearchValue] = useState<string>("");
    const [peopleData, setPeopleData] = useState<PeopleDtos.PeopleSearchRow[]>([]);
    const [person, setPerson] = useState<PeopleDtos.PeopleSearchRow | null>(null);
    const [peopleLoading, setPeopleLoading] = useState<boolean>(false);

    const statusOptions: IDropDownOptions[] =
        [
            { id: "Active", value: true },
            { id: "Resigned", value: false },
        ]

    const { control, reset, handleSubmit, formState: { errors, isSubmitting }, setValue, getValues } = useForm<CommitteeMember.DtoOut>({
        mode: 'all',
        defaultValues: {
            scorecardId: props.scorecardId,
            committeeType: props.committeeType,
            isTopManagement: false,
            isSeniorManagement: false,
            isMiddleManagement: false,
            isJuniorManagement: false,
            isSemiSkilled: false,
            isUnskilled: false,
            isDisabled: false,
            isAfrican: false,
            isColoured: false,
            isIndian: false,
            isWhite: false,
            isNonSA: false,
            isMale: false,
            isFemale: false,
            isActive: true,
            isUnionRepresentative: false,
            dateAppointed: new Date(getDateWithoutTime(new Date()).toDateString()),
            dateResigned: null
        },
        resolver: yupResolver(committeeMemberPersonSchema)
    });

    let peopleSearchRequest: PeopleDtos.PeopleSearchRequest = {
        pageNumber: 1,
        pageSize: 20,
        searchValue: peopleSearchValue,
        registrationId: emptyGuid,
        scorecardIds: [],
        genderIds: [],
        raceIds: [],
        employeeTypeIds: [],
        disabledValues: [],
        occupationalLevelIds: [],
        provinceIds: [],
        isYesEmployeeValues: [],
        isYesAbsorbedValues: [],
        startDateFrom: undefined,
        startDateTo: undefined,
        endDateFrom: undefined,
        endDateTo: undefined,
        columnSortOptions: []
    }

    const fetchPeopleData = useAsyncDebounce(async () => {

        if (peopleSearchRequest.searchValue !== '') {
            try {
                setPeopleLoading(true);
                let resp = await PeopleService.SearchPeople(peopleSearchRequest);
                if (resp.data) {
                    setPeopleData(resp.data.results);

                }
            } catch (error) {
                console.log(error);
            } finally {
                setPeopleLoading(false);
            }
        }
        else {
            setPeopleData([]);
        }
    }, 300);

    const setPersonDetails = async (personId: number) => {
        await getPersonById(personId);
        const positionData = await PeopleService.GetLatestPositionForPerson(personId);
        if (positionData.data) {
            setJobTitle((positionData.data.positionName === "" || positionData.data.positionName === null) ? "Unspecified" : positionData.data.positionName);
            setOccupationalLevel(occupationLevelFormatter(positionData.data.occupationalLevelId));
        }
        else {
            setJobTitle("Unspecified");
            setOccupationalLevel("Unspecified");
        }
    }

    const getPersonById = async (personId: number) => {
        const person = await PeopleService.GetPeople(personId);
        const personRow = { createdDate: new Date(), createdBy: "", ...person.data } as PeopleDtos.PeopleSearchRow;
        setPerson(personRow);
        setPeopleSearchValue(personRow.fullName);
    }


    const handleDataSubmit: SubmitHandler<CommitteeMember.DtoOut> = async (member: CommitteeMember.DtoOut) => {
        member.dateAppointed = offsetTimezone(member.dateAppointed);
        if (member.dateResigned !== null) {
            member.dateResigned = offsetTimezone(member.dateResigned);
        }
        try {
            if (!memberId) {
                const result = await CommitteeService.CreateMember(member);
                toast.success("Member added succesfully");
                const saveMemberId = result.data.id;
                setMemberId(saveMemberId);
                setValue("id", saveMemberId);
                props.setMemberId(saveMemberId);
                setMemberId(saveMemberId);
                handleCancel();
            }
            else {
                await CommitteeService.UpdateMember(member);
                toast.success("Member saved succesfully");
                handleCancel();
            }

        } catch (error) {
            console.log(error)
        }
    }

    const handleErrors = (errors: any, e: any) => {
        for (let key in errors) {
            if (errors.hasOwnProperty(key)) {
                toast.error(errors[key].message);
            }
        }
    }

    const handleCancel = () => {
        props.handleCancel();
        props.refreshTable();
    }

    const openDocumentsPopUp = () => {
        setDocumentsPopUp(true);
    }

    const getMember = async (id: string) => {
        try {
            const result = await CommitteeService.GetMember(id);
            var member =
                {
                    id: result.data.id,
                    personId: result.data.personId,
                    scorecardId: props.scorecardId,
                    isTopManagement: result.data.isTopManagement,
                    isSeniorManagement: result.data.isSeniorManagement,
                    isMiddleManagement: result.data.isMiddleManagement,
                    isJuniorManagement: result.data.isJuniorManagement,
                    isSemiSkilled: result.data.isSemiSkilled,
                    isUnskilled: result.data.isUnskilled,
                    isDisabled: result.data.isDisabled,
                    isAfrican: result.data.isAfrican,
                    isColoured: result.data.isColoured,
                    isIndian: result.data.isIndian,
                    isWhite: result.data.isWhite,
                    isNonSA: result.data.isNonSA,
                    isMale: result.data.isMale,
                    isFemale: result.data.isFemale,
                    isUnionRepresentative: result.data.isUnionRepresentative,
                    dateAppointed: new Date(new Date(result.data.dateAppointed).toDateString()),
                    isActive: result.data.isActive,
                    dateResigned: result.data.dateResigned === null ? null : new Date(new Date(result.data.dateResigned).toDateString()),
                    committeeType: props.committeeType
                } as CommitteeMember.DtoOut
            reset(member);
            setShowResignedDate(result.data.isActive === false);
            setPersonDetails(member.personId);
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        if (memberId) {
            getMember(memberId);
        }
    }, []);

    useEffect(() => {
        fetchPeopleData();
    }, [peopleSearchValue]);


    return (
        <Box >
            <Dialog open={props.previewPopup} onClose={handleCancel} aria-labelledby="form-dialog-title" maxWidth='md' fullWidth>
                <CustomDialogTitle id="customized-dialog-title" onClose={handleCancel}>
                    {props.id !== undefined ? "Edit Member" : "Add Member"}
                </CustomDialogTitle>
                <DialogContent>
                    <Grid container justifyContent='center' sx={{ my: 2 }}>
                        <Grid item xs={10} lg={10}>
                            <Box component="form" id='CommitteeMemberForm' onSubmit={handleSubmit(handleDataSubmit, handleErrors)}>
                                <MDBox>
                                    <MDBox mb={1}>
                                        <Grid item xs={12}>
                                            <MDBox mb={2}>
                                                <Controller
                                                    control={control}
                                                    name="personId"
                                                    render={({ field: { onChange, value } }) => (
                                                        <Autocomplete
                                                            noOptionsText={'No people matching the search input were found'}
                                                            loading={peopleLoading}
                                                            options={peopleData}
                                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                                            value={person}
                                                            getOptionLabel={option => option.fullName}
                                                            onChange={(e: any, newValue: any) => {
                                                                onChange(newValue?.id);
                                                                setValue("personId", newValue?.id);
                                                                if (newValue) {
                                                                    setPersonDetails(newValue.id);
                                                                }
                                                                else {
                                                                    setPeopleSearchValue("");
                                                                    setJobTitle("");
                                                                    setOccupationalLevel("");
                                                                    setPerson(null);
                                                                }
                                                            }}
                                                            renderOption={(props, option) => {
                                                                return (
                                                                    <li {...props} key={option.id}>
                                                                        {option.fullName}
                                                                    </li>
                                                                );
                                                            }}
                                                            renderInput={(params) =>
                                                                <TextField label='Member *' {...params} type='text'
                                                                    placeholder="Search for person"
                                                                    error={!!errors.personId}
                                                                    helperText={errors.personId?.message}
                                                                    variant="standard"
                                                                    onChange={(e: any) => {
                                                                        if (e.target.value !== null) {
                                                                            setPeopleSearchValue(e.target.value);
                                                                        }
                                                                    }}
                                                                />
                                                            }
                                                        />

                                                    )}
                                                />
                                            </MDBox>
                                            <MDBox mb={2}>
                                                <TextField value={person === null ? "" : person.race} InputLabelProps={{ shrink: true, style: { color: '#000000' } }} type='text' label='Race' fullWidth variant="standard" size="medium" disabled />
                                            </MDBox>
                                            <MDBox mb={2}>
                                                <TextField value={person === null ? "" : person.gender} InputLabelProps={{ shrink: true, style: { color: '#000000' } }} type='text' label='Gender' fullWidth variant="standard" size="medium" disabled />
                                            </MDBox>
                                            <MDBox mb={2}>
                                                <TextField value={occupationalLevel} InputLabelProps={{ shrink: true, style: { color: '#000000' } }} type='text' label='Occupational Level' fullWidth variant="standard" size="medium" disabled />
                                            </MDBox>
                                            <MDBox mb={2}>
                                                <TextField value={jobTitle} InputLabelProps={{ shrink: true, style: { color: '#000000' } }} type='text' label='Job Title' fullWidth variant="standard" size="medium" disabled />
                                            </MDBox>
                                            <MDBox mb={3} >
                                                <MDTypography
                                                    variant='button'
                                                    fontWeight='regular'
                                                    style={{ color: '#000000', fontSize: "0.80em" }} >
                                                    Constituency Represented
                                                </MDTypography>
                                                <MDBox mb={3} ml={2}>
                                                    <Grid container>
                                                        <Grid item sm={6} md={6} lg={6}>
                                                            <MDBox mt={1} mb={0.5}>
                                                                <MDTypography
                                                                    variant='button'
                                                                    fontWeight='regular'
                                                                    style={{ fontSize: "0.9em" }} >
                                                                    Occupational Level:
                                                                </MDTypography>
                                                            </MDBox>
                                                            <Controller
                                                                control={control}
                                                                name="isTopManagement"
                                                                render={({ field: { onChange, value } }) => (
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={value}
                                                                                onChange={(e: any) => {
                                                                                    onChange(e.target.checked);
                                                                                    setValue("isTopManagement", e.target.checked);
                                                                                }}
                                                                                color="secondary"
                                                                            />
                                                                        } label="Top Management"
                                                                        style={{ fontSize: "0.75em" }}
                                                                    />
                                                                )}
                                                            />
                                                            <Controller
                                                                control={control}
                                                                name="isSeniorManagement"
                                                                render={({ field: { onChange, value } }) => (
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={value}
                                                                                onChange={(e: any) => {
                                                                                    onChange(e.target.checked);
                                                                                    setValue("isSeniorManagement", e.target.checked);
                                                                                }}
                                                                                color="secondary"
                                                                            />
                                                                        } label="Senior Management"
                                                                    />
                                                                )}
                                                            />
                                                            <Controller
                                                                control={control}
                                                                name="isMiddleManagement"
                                                                render={({ field: { onChange, value } }) => (
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={value}
                                                                                onChange={(e: any) => {
                                                                                    onChange(e.target.checked);
                                                                                    setValue("isMiddleManagement", e.target.checked);
                                                                                }}
                                                                                color="secondary"
                                                                            />
                                                                        } label="Middle Management"
                                                                    />
                                                                )}
                                                            />
                                                            <Controller
                                                                control={control}
                                                                name="isJuniorManagement"
                                                                render={({ field: { onChange, value } }) => (
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={value}
                                                                                onChange={(e: any) => {
                                                                                    onChange(e.target.checked);
                                                                                    setValue("isJuniorManagement", e.target.checked);
                                                                                }}
                                                                                color="secondary"
                                                                            />
                                                                        } label="Junior Management"
                                                                    />
                                                                )}
                                                            />
                                                            <Controller
                                                                control={control}
                                                                name="isSemiSkilled"
                                                                render={({ field: { onChange, value } }) => (
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={value}
                                                                                onChange={(e: any) => {
                                                                                    onChange(e.target.checked);
                                                                                    setValue("isSemiSkilled", e.target.checked);
                                                                                }}
                                                                                color="secondary"
                                                                            />
                                                                        } label="Semi-Skilled"
                                                                    />
                                                                )}
                                                            />
                                                            <Controller
                                                                control={control}
                                                                name="isUnskilled"
                                                                render={({ field: { onChange, value } }) => (
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={value}
                                                                                onChange={(e: any) => {
                                                                                    onChange(e.target.checked);
                                                                                    setValue("isUnskilled", e.target.checked);
                                                                                }}
                                                                                color="secondary"
                                                                            />
                                                                        } label="Unskilled"
                                                                    />
                                                                )}
                                                            />
                                                            <MDBox mt={1} mb={0.5}>
                                                                <MDTypography
                                                                    variant='button'
                                                                    fontWeight='regular'
                                                                    style={{ fontSize: "0.9em" }} >
                                                                    Disabled:
                                                                </MDTypography>
                                                            </MDBox>
                                                            <Controller
                                                                control={control}
                                                                name="isDisabled"
                                                                render={({ field: { onChange, value } }) => (
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={value}
                                                                                onChange={(e: any) => {
                                                                                    onChange(e.target.checked);
                                                                                    setValue("isDisabled", e.target.checked);
                                                                                }}
                                                                                color="secondary"
                                                                            />
                                                                        } label="Disabled"
                                                                        style={{ fontSize: "0.75em" }}
                                                                    />
                                                                )}
                                                            />
                                                            <MDBox mt={1} mb={0.5}>
                                                                <MDTypography
                                                                    variant='button'
                                                                    fontWeight='regular'
                                                                    style={{ fontSize: "0.9em" }} >
                                                                    Other:
                                                                </MDTypography>
                                                            </MDBox>
                                                            <Controller
                                                                control={control}
                                                                name="isUnionRepresentative"
                                                                render={({ field: { onChange, value } }) => (
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={value}
                                                                                onChange={(e: any) => {
                                                                                    onChange(e.target.checked);
                                                                                    setValue("isUnionRepresentative", e.target.checked);
                                                                                }}
                                                                                color="secondary"
                                                                            />
                                                                        } label="Union Representative"
                                                                        style={{ fontSize: "0.75em" }}
                                                                    />
                                                                )}
                                                            />
                                                        </Grid>
                                                        <Grid item sm={6} md={6} lg={6}>
                                                            <MDBox mt={1} mb={0.5}>
                                                                <MDTypography
                                                                    variant='button'
                                                                    fontWeight='regular'
                                                                    style={{ fontSize: "0.9em" }} >
                                                                    Race:
                                                                </MDTypography>
                                                            </MDBox>
                                                            <Controller
                                                                control={control}
                                                                name="isAfrican"
                                                                render={({ field: { onChange, value } }) => (
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={value}
                                                                                onChange={(e: any) => {
                                                                                    onChange(e.target.checked);
                                                                                    setValue("isAfrican", e.target.checked);
                                                                                }}
                                                                                color="secondary"
                                                                            />
                                                                        } label="African"
                                                                        style={{ fontSize: "0.75em" }}
                                                                    />
                                                                )}
                                                            />
                                                            <Controller
                                                                control={control}
                                                                name="isColoured"
                                                                render={({ field: { onChange, value } }) => (
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={value}
                                                                                onChange={(e: any) => {
                                                                                    onChange(e.target.checked);
                                                                                    setValue("isColoured", e.target.checked);
                                                                                }}
                                                                                color="secondary"
                                                                            />
                                                                        } label="Coloured"
                                                                    />
                                                                )}
                                                            />
                                                            <Controller
                                                                control={control}
                                                                name="isIndian"
                                                                render={({ field: { onChange, value } }) => (
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={value}
                                                                                onChange={(e: any) => {
                                                                                    onChange(e.target.checked);
                                                                                    setValue("isIndian", e.target.checked);
                                                                                }}
                                                                                color="secondary"
                                                                            />
                                                                        } label="Indian"
                                                                    />
                                                                )}
                                                            />
                                                            <Controller
                                                                control={control}
                                                                name="isWhite"
                                                                render={({ field: { onChange, value } }) => (
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={value}
                                                                                onChange={(e: any) => {
                                                                                    onChange(e.target.checked);
                                                                                    setValue("isWhite", e.target.checked);
                                                                                }}
                                                                                color="secondary"
                                                                            />
                                                                        } label="White"
                                                                    />
                                                                )}
                                                            />
                                                            <Controller
                                                                control={control}
                                                                name="isNonSA"
                                                                render={({ field: { onChange, value } }) => (
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={value}
                                                                                onChange={(e: any) => {
                                                                                    onChange(e.target.checked);
                                                                                    setValue("isNonSA", e.target.checked);
                                                                                }}
                                                                                color="secondary"
                                                                            />
                                                                        } label="Non South African"
                                                                    />
                                                                )}
                                                            />
                                                            <MDBox mt={1} mb={0.5}>
                                                                <MDTypography
                                                                    variant='button'
                                                                    fontWeight='regular'
                                                                    style={{ fontSize: "0.9em" }} >
                                                                    Gender:
                                                                </MDTypography>
                                                            </MDBox>
                                                            <Controller
                                                                control={control}
                                                                name="isMale"
                                                                render={({ field: { onChange, value } }) => (
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={value}
                                                                                onChange={(e: any) => {
                                                                                    onChange(e.target.checked);
                                                                                    setValue("isMale", e.target.checked);
                                                                                }}
                                                                                color="secondary"
                                                                            />
                                                                        } label="Male"
                                                                        style={{ fontSize: "0.75em" }}
                                                                    />
                                                                )}
                                                            />
                                                            <Controller
                                                                control={control}
                                                                name="isFemale"
                                                                render={({ field: { onChange, value } }) => (
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={value}
                                                                                onChange={(e: any) => {
                                                                                    onChange(e.target.checked);
                                                                                    setValue("isFemale", e.target.checked);
                                                                                }}
                                                                                color="secondary"
                                                                            />
                                                                        } label="Female"
                                                                        style={{ fontSize: "0.75em" }}
                                                                    />
                                                                )}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </MDBox>
                                            </MDBox>
                                            <MDBox mb={2}>
                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    <FormControl fullWidth>
                                                        <Controller
                                                            control={control}
                                                            name="dateAppointed"
                                                            render={({ field: { onChange, value } }) => (
                                                                <ValidatedMobileDatePicker
                                                                    inputFormat="yyyy/MM/dd"
                                                                    mask="____/__/__"
                                                                    label="Date Appointed *"
                                                                    value={value}
                                                                    onChange={(date: Date) => {

                                                                        if (date != null) {
                                                                            onChange(getDateWithoutTime(date));
                                                                            setValue("dateAppointed", getDateWithoutTime(date));
                                                                        }
                                                                    }}
                                                                    renderInput={(params: any) => <TextField {...params}
                                                                        error={!!errors.dateAppointed}
                                                                        helperText={errors.dateAppointed?.message}
                                                                    />
                                                                    }
                                                                    views={['year', 'month', 'day']}
                                                                />
                                                            )}
                                                        />
                                                    </FormControl>
                                                </LocalizationProvider>
                                            </MDBox>
                                            <MDBox mb={2} ml={1}>
                                                <Controller
                                                    control={control}
                                                    name="isActive"
                                                    render={({ field: { onChange, value } }) => (
                                                        <FormControl>
                                                            <MDTypography
                                                                variant='button'
                                                                fontWeight='regular'
                                                                style={{ color: '#000000', fontSize: "0.80em" }} >
                                                                Status
                                                            </MDTypography>
                                                            <RadioGroup
                                                                row={true}
                                                                value={value}
                                                                name="radio-buttons-group"
                                                                onChange={(e: any) => {
                                                                    onChange(e.target.value);
                                                                    setValue("isActive", e.target.value);
                                                                    setShowResignedDate(e.target.value === "false");
                                                                }}
                                                            >
                                                                {statusOptions.map((item) => (
                                                                    <FormControlLabel key={item.id} value={item.value} control={<Radio size="small" />} label={item.id} />
                                                                ))}

                                                            </RadioGroup>
                                                            <FormHelperText>{errors.isActive?.message}</FormHelperText>
                                                        </FormControl>
                                                    )}
                                                />
                                            </MDBox>
                                            {showResignedDate && <MDBox mb={2}>
                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    <FormControl fullWidth>
                                                        <Controller
                                                            control={control}
                                                            name="dateResigned"
                                                            render={({ field: { onChange, value } }) => (
                                                                <ValidatedMobileDatePicker
                                                                    inputFormat="yyyy/MM/dd"
                                                                    mask="____/__/__"
                                                                    label="Date Resigned *"
                                                                    value={value}
                                                                    onChange={(date: Date) => {
                                                                        if (date != null) {
                                                                            onChange(getDateWithoutTime(date));
                                                                            setValue("dateResigned", getDateWithoutTime(date));
                                                                        }
                                                                    }}
                                                                    renderInput={(params: any) => <TextField {...params}
                                                                        error={!!errors.dateResigned}
                                                                        helperText={errors.dateResigned?.message}
                                                                    />
                                                                    }
                                                                    views={['year', 'month', 'day']}
                                                                />
                                                            )}
                                                        />
                                                    </FormControl>
                                                </LocalizationProvider>
                                            </MDBox>}
                                        </Grid>
                                    </MDBox>
                                </MDBox>
                            </Box>
                        </Grid>
                    </Grid>
                    {memberId !== undefined && <SupportingDocuments display={documentsPopUp} handleCancel={() => setDocumentsPopUp(false)} link={props.link} id={memberId} scorecardId={props.scorecardId} isEEModule={true} />}
                </DialogContent>
                <DialogActions>
                    {memberId !== undefined &&
                        <MDButton variant="contained" color="primary" onClick={openDocumentsPopUp} startIcon={<AttachFileIcon />}
                            size="medium">
                            Attach File
                        </MDButton>
                    }
                    <LoadingButton variant='contained' color='primary' type='submit' form='CommitteeMemberForm' loading={isSubmitting}>
                        Save
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Box>);
}
